import React from 'react';
import { Chip, useTheme } from '@mui/material';
import { AdminPanelSettings, BugReport, Person, HourglassTop, Shield } from '@mui/icons-material';
import { UserRole, USER_ROLES } from '@watchlist/shared';

interface UserRoleChipProps {
  role: UserRole;
  size?: 'small' | 'medium';
}

const UserRoleChip: React.FC<UserRoleChipProps> = ({ role, size = 'small' }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const getRoleConfig = (role: UserRole) => {
    switch (role) {
      case USER_ROLES.ADMIN:
        return {
          label: 'Admin',
          icon: <AdminPanelSettings sx={{ fontSize: size === 'small' ? 14 : 18 }} />,
          bgcolor: isDark ? 'rgba(255, 69, 58, 0.2)' : 'rgba(255, 69, 58, 0.1)',
          color: '#FF453A',
          borderColor: '#FF453A',
        };
      case USER_ROLES.MODERATOR:
        return {
          label: 'Moderator',
          icon: <Shield sx={{ fontSize: size === 'small' ? 14 : 18 }} />,
          bgcolor: isDark ? 'rgba(10, 132, 255, 0.22)' : 'rgba(0, 122, 255, 0.12)',
          color: '#0A84FF',
          borderColor: '#0A84FF',
        };
      case USER_ROLES.TEST:
        return {
          label: 'Tester',
          icon: <BugReport sx={{ fontSize: size === 'small' ? 14 : 18 }} />,
          bgcolor: isDark ? 'rgba(255, 159, 10, 0.2)' : 'rgba(255, 159, 10, 0.1)',
          color: '#FF9F0A',
          borderColor: '#FF9F0A',
        };
      case USER_ROLES.PENDING:
        return {
          label: 'Pending',
          icon: <HourglassTop sx={{ fontSize: size === 'small' ? 14 : 18 }} />,
          bgcolor: isDark ? 'rgba(142, 142, 147, 0.2)' : 'rgba(142, 142, 147, 0.1)',
          color: '#8E8E93',
          borderColor: '#8E8E93',
        };
      case USER_ROLES.REGULAR:
      default:
        return {
          label: 'User',
          icon: <Person sx={{ fontSize: size === 'small' ? 14 : 18 }} />,
          bgcolor: isDark ? 'rgba(48, 209, 88, 0.2)' : 'rgba(48, 209, 88, 0.1)',
          color: '#30D158',
          borderColor: '#30D158',
        };
    }
  };

  const config = getRoleConfig(role);

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      size={size}
      sx={{
        bgcolor: config.bgcolor,
        color: config.color,
        border: `1px solid ${config.borderColor}40`,
        fontWeight: 500,
        fontSize: size === 'small' ? 11 : 13,
        height: size === 'small' ? 24 : 32,
        '& .MuiChip-icon': {
          color: config.color,
        },
      }}
    />
  );
};

export default UserRoleChip;
